import { useEffect } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/turboclaim-logo.png";
import { PlanHeader } from "@/components/subscription/PlanHeader";
import { PlanCardsGrid } from "@/components/subscription/PlanCardsGrid";
import { useSubscriptionPlans } from "@/hooks/use-subscription-plans";

export default function Plans() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const {
    user,
    tiers,
    subscription,
    tiersLoading,
    billingInterval,
    selectedPlan,
    setBillingInterval,
    handleSelectPlan,
    getCurrentTierPrice,
    isAdmin,
  } = useSubscriptionPlans({ redirectPath: 'onboarding' });

  // Check message from previous page (e.g., "subscription required")
  useEffect(() => {
    const message = sessionStorage.getItem("subscriptionMessage");
    if (message) {
      toast({
        title: "Subscription Required",
        description: message,
      });
      sessionStorage.removeItem("subscriptionMessage");
    }
  }, [toast]);

  // Redirect if not authenticated
  useEffect(() => {
    if (user === undefined) return; // Still loading
    if (!user) {
      setLocation("/login");
      return;
    }
  }, [user, setLocation]);

  // Handle successful subscription - redirect to workspace
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get('success') === 'true';
    const isCanceled = params.get('canceled') === 'true';

    if (isSuccess && subscription) {
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);

      // Show success message
      toast({
        title: "Welcome to TurboClaim!",
        description: "Your subscription is active. Let's get started.",
      });

      // Redirect to workspace
      setTimeout(() => {
        setLocation("/");
      }, 1000);
    } else if (isCanceled) {
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);

      // Show canceled message
      toast({
        title: "Checkout Canceled",
        description: "No worries! You can select a plan whenever you're ready.",
        variant: "default",
      });
    }
  }, [subscription, toast, setLocation]);

  // Show loading state
  if (!user || tiersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-pulse">
            <img src={logo} alt="TurboClaim" className="h-12 mx-auto mb-4 opacity-60" />
          </div>
          <p className="text-slate-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <img src={logo} alt="TurboClaim" className="h-12 mx-auto mb-6" />
        </div>

        {/* Plan Header with Billing Interval Toggle */}
        <PlanHeader
          billingInterval={billingInterval}
          onBillingIntervalChange={setBillingInterval}
        />

        {/* Plan Cards Grid */}
        <div className="mt-12">
          <PlanCardsGrid
            tiers={tiers}
            isLoading={tiersLoading}
            billingInterval={billingInterval}
            currentTierCode={subscription?.tierCode}
            currentTierPrice={getCurrentTierPrice()}
            hasCompany={!!user?.companyId}
            processingPlanCode={selectedPlan}
            onSelectPlan={handleSelectPlan}
            isAdmin={isAdmin}
          />
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>
            Secure checkout powered by Stripe • All major credit cards accepted
          </p>
        </div>
      </div>
    </div>
  );
}
